// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: plan usage of a person's coding agents (Claude, Codex, ...), merged across their machines.
// Each machine's konvo-agents reports what its agent tool last checked (AgentPlanUsageSchema); one
// login on two machines is one plan here, and the newest check wins. Shared by the API (assistant
// tool, warnings, Team insights) and the app (Agents icon, list footer, phone chips).

import type {AgentPlanUsage, AgentToolInfo, AgentUsageWindow} from '@konvo/schema/src/domains/konvo/Agents';

/** From here the Agents icon carries a meter and bars turn amber. */
export const AGENT_USAGE_WARN_PERCENT = 80;
/** Konvo tells you once per window when it gets this far (Settings -> Notifications -> Agents). */
export const AGENT_USAGE_ALERT_PERCENT = 90;

export interface AgentUsageWindowView extends AgentUsageWindow {
	/** The window reset after the last check: it reads 0% until the tool checks again. */
	reset?: boolean;
}

export interface AgentPlanUsageView extends Omit<AgentPlanUsage, 'windows'> {
	/** Stable per plan: the account hash, or provider + machine when the tool sent none. */
	key: string;
	windows: Array<AgentUsageWindowView>;
	/** Names of the machines that use this plan. */
	machines: Array<string>;
}

export type AgentUsageTone = 'ok' | 'warn' | 'out';

export function agentUsageTone(percent: number): AgentUsageTone {
	if (percent >= 100) return 'out';
	if (percent >= AGENT_USAGE_WARN_PERCENT) return 'warn';
	return 'ok';
}

export interface MachineLike {
	name: string;
	tools: ReadonlyArray<Pick<AgentToolInfo, 'usage'>>;
}

/** Every plan the person's machines report, one per account, Claude before Codex (by name). */
export function mergeAgentPlanUsage(machines: ReadonlyArray<MachineLike>, now = Date.now()): Array<AgentPlanUsageView> {
	const plans = new Map<string, AgentPlanUsageView>();
	for (const machine of machines) {
		for (const tool of machine.tools) {
			for (const usage of tool.usage ?? []) {
				const key = usage.account ? `${usage.provider}:${usage.account}` : `${usage.provider}@${machine.name}`;
				const seen = plans.get(key);
				const names = seen ? [...new Set([...seen.machines, machine.name])] : [machine.name];
				if (seen && Date.parse(seen.checkedAt) >= Date.parse(usage.checkedAt)) {
					seen.machines = names;
					continue;
				}
				plans.set(key, {
					...usage,
					key,
					machines: names,
					windows: usage.windows.map((w) => resetWindow(w, now)),
				});
			}
		}
	}
	return [...plans.values()].sort((a, b) => a.providerName.localeCompare(b.providerName) || a.key.localeCompare(b.key));
}

/** "claudeAgent" (a plan) and "claude" (a thread) are the same agent. */
export const agentFamily = (provider: string) =>
	/claude/i.test(provider) ? 'claude' : /codex/i.test(provider) ? 'codex' : provider.toLowerCase();

export interface ThreadOnMachine {
	/** The agent family from the thread summary: claude, codex, ... */
	provider: string;
	/** The tool's provider instance the thread runs on (its model choice; konvo-agents 0.13.0+). */
	instance?: string;
}

/**
 * The plan a thread runs on. One machine can have two logins of one agent (two T3 Claude
 * instances, each with its own home), so the thread's instance picks the login its machine
 * reports, and that login's plan. Threads from older bridges, or on machines that report no
 * login for the instance, fall back to the first plan of the same agent.
 */
export function agentThreadPlan<P extends AgentPlanUsageView>(
	plans: ReadonlyArray<P>,
	machine: MachineLike | null | undefined,
	thread: ThreadOnMachine,
): P | null {
	const family = agentFamily(thread.provider);
	const instance = thread.instance;
	if (instance && machine) {
		const reported = machine.tools.flatMap((tool) => tool.usage ?? []);
		const usage = reported.find((entry) => entry.provider === instance);
		if (usage) {
			const key = usage.account ? `${usage.provider}:${usage.account}` : `${usage.provider}@${machine.name}`;
			const plan =
				plans.find((entry) => entry.key === key) ??
				(usage.account
					? plans.find((entry) => entry.account === usage.account && agentFamily(entry.provider) === family)
					: undefined);
			if (plan) return plan;
		} else if (reported.some((entry) => agentFamily(entry.provider) === family)) {
			// Another login of this agent reports a plan here; this instance has none (an API key).
			return null;
		}
	}
	return (
		(instance ? plans.find((entry) => entry.provider === instance) : undefined) ??
		plans.find((entry) => agentFamily(entry.provider) === family) ??
		null
	);
}

function resetWindow(window: AgentUsageWindow, now: number): AgentUsageWindowView {
	const resetsAt = window.resetsAt ? Date.parse(window.resetsAt) : Number.NaN;
	if (Number.isNaN(resetsAt) || resetsAt > now) return window;
	const {resetsAt: _gone, ...rest} = window;
	return {...rest, usedPercent: 0, reset: true};
}

/** The window closest to running out (ties: the shorter one), for one-number summaries. */
export function tightestAgentWindow(plan: Pick<AgentPlanUsageView, 'windows'>): AgentUsageWindowView | null {
	let best: AgentUsageWindowView | null = null;
	for (const w of plan.windows) {
		if (
			!best ||
			w.usedPercent > best.usedPercent ||
			(w.usedPercent === best.usedPercent && (w.windowMinutes ?? Infinity) < (best.windowMinutes ?? Infinity))
		)
			best = w;
	}
	return best;
}

/**
 * When the window runs out at the pace used so far, if that is well before it resets. Needs the
 * window's length and reset time, 15 minutes into the window and at least 20% used, so an early
 * burst does not cry wolf. Returns an epoch ms, or null.
 */
export function agentWindowRunsOutAt(window: AgentUsageWindow, now = Date.now()): number | null {
	if (!window.resetsAt || !window.windowMinutes || window.usedPercent < 20 || window.usedPercent >= 100) return null;
	const resetsAt = Date.parse(window.resetsAt);
	if (Number.isNaN(resetsAt) || resetsAt <= now) return null;
	const elapsed = window.windowMinutes * 60_000 - (resetsAt - now);
	if (elapsed < 15 * 60_000) return null;
	const at = now + ((100 - window.usedPercent) / window.usedPercent) * elapsed;
	return at < resetsAt - 10 * 60_000 ? at : null;
}

/** The first window of the plan that runs out early at this pace, with when. */
export function agentPlanRunsOut(
	plan: Pick<AgentPlanUsageView, 'windows'>,
	now = Date.now(),
): {window: AgentUsageWindowView; at: number} | null {
	let first: {window: AgentUsageWindowView; at: number} | null = null;
	for (const w of plan.windows) {
		const at = agentWindowRunsOutAt(w, now);
		if (at !== null && (!first || at < first.at)) first = {window: w, at};
	}
	return first;
}

export type AgentUsagePace = 'under' | 'on' | 'over';

/**
 * How the used share compares with the share of the window that has passed: more than 5 points
 * ahead is over pace, more than 5 behind is under. Needs the window's length and reset time; null
 * for a used-up window or in the first 5% of one, where any use reads as a sprint.
 */
export function agentWindowPace(window: AgentUsageWindow, now = Date.now()): AgentUsagePace | null {
	if (!window.resetsAt || !window.windowMinutes || window.usedPercent >= 100) return null;
	const resetsAt = Date.parse(window.resetsAt);
	if (Number.isNaN(resetsAt) || resetsAt <= now) return null;
	const length = window.windowMinutes * 60_000;
	const elapsed = Math.min(100, ((length - (resetsAt - now)) / length) * 100);
	if (elapsed < 5) return null;
	const ahead = window.usedPercent - elapsed;
	return ahead > 5 ? 'over' : ahead < -5 ? 'under' : 'on';
}

/** "Max" from "Claude Max", "Business" from "ChatGPT Business": the plan without the brand. */
export function agentPlanShortName(plan: Pick<AgentPlanUsage, 'plan' | 'providerName'>): string | undefined {
	if (!plan.plan) return undefined;
	const short = plan.plan.replace(
		new RegExp(`^(${escapeRegExp(plan.providerName)}|ChatGPT|Claude|OpenAI)\\s+`, 'i'),
		'',
	);
	return short || plan.plan;
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
